/**
 * The in-app feedback dialog (docs/project/user-feedback-feature.md).
 *
 * Two doors — the tabs-row 💬 beside the "?" and one on every Learn screen — into ONE dialog. The
 * app never asks for feedback: there is no prompt here, no finish-screen nag and no "rate us". Both
 * controls sit still until somebody reaches for them, which is why every message that arrives is
 * worth reading.
 *
 * Built on the msgbox chassis the way onboarding's cards are (.msgOverlayClass backdrop +
 * .msgBoxClass box + own .fbCardClass), NOT via showMessage: that box takes textContent and traps
 * focus on its single OK, and this one holds five controls.
 *
 * Two things worth knowing before changing it:
 *
 *  - The DOM is built ONCE and re-shown. An accidental Esc or a click on the backdrop must never
 *    cost somebody the paragraph they just typed, so the dialog keeps its own state between opens
 *    and is only cleared after a message actually lands.
 *  - The context snapshot is taken at OPEN, not at Send. The state the user reached for the button
 *    in is the state they are reporting about; by the time they finish typing they may well have
 *    clicked elsewhere.
 *  - The five badges WRITE INTO the message (owner call 2026-08-17). Each one drops its own heading
 *    — "🐞 Problem:" — at the end of the text and puts the caret after it, so one message can carry
 *    a problem AND a suggestion instead of forcing the writer to pick the nearest one. There is no
 *    `pickedType` variable any more: which badges are lit and which type the server is told about
 *    are both DERIVED from the text, so hand-editing a heading away is as good as un-picking it and
 *    no stored choice can outlive what the box actually says.
 */
(function () {
	"use strict";

	// Reading order, owner call 2026-08-17: question · suggestion · like · dislike · problem —
	// cheapest to answer first, most work last, and the two positive ones between the two
	// negative ones rather than after them. It is NOT the enum's declaration order, which is
	// frozen by rows already stored. IDEA keeps its stored name and is LABELLED "Suggestion".
	var TYPES = ["QUESTION", "IDEA", "LIKE", "DISLIKE", "PROBLEM"];
	var GLYPH = { QUESTION: "❓", IDEA: "💡", LIKE: "👍", DISLIKE: "👎", PROBLEM: "🐞" };

	var overlay = null;      // the built-once backdrop; kept detached between opens
	var box = null;
	var typeRow = null;
	var textArea = null;
	var emailField = null;   // null when the account already has an email
	var sendBtn = null;
	var layer = null;        // the open dialog's PL.layers handle (null = closed)
	var context = null;      // the snapshot taken at open time
	var sending = false;

	function el(tag, cls, text) {
		var node = document.createElement(tag);
		if (cls) { node.className = cls; }
		if (text != null) { node.textContent = text; }
		return node;
	}

	// ── The context snapshot ─────────────────────────────────────────────────────────────────
	// Everything here is best-effort: a missing field is better than a dialog that fails to open.
	// The server adds the user, the user-agent and the timestamp.
	function collectContext(source) {
		var ctx = { source: source };
		try {
			var view = window.PL_VIEW_STATE ? window.PL_VIEW_STATE() : null;
			if (view) {
				ctx.tab = view.tab;
				ctx.exercise = !!view.exercise;
				if (view.word) { ctx.word = String(view.word); }
			}
			ctx.langFrom = $("#langFromValId").attr("data-lang-code") || null;
			ctx.langTo = $("#langToValId").attr("data-lang-code") || null;
			ctx.viewport = window.innerWidth + "x" + window.innerHeight;
		} catch (e) {
			// A snapshot is a nicety; the message is the point.
		}
		return ctx;
	}

	// ── Rubrics: the badges' headings, and everything derived from them ──────────────────────
	// A badge's heading, exactly as it appears in the message. This one function is both the
	// writer and the reader: everything below finds rubrics by searching for what it produced,
	// so the two can never drift apart.
	function rubric(type) {
		return GLYPH[type] + " " + getRes("feedback_type_" + type.toLowerCase()) + ":";
	}

	// ── The message field ────────────────────────────────────────────────────────────────────
	// It is a contenteditable div, not a textarea (owner call 2026-08-17): a heading has to LOOK
	// like the app wrote it — bold, subdued — and be untypeable-into, and a textarea holds nothing
	// but characters that any keystroke may edit. So the headings are real elements
	// (contenteditable="false"), each section is a block, and everything downstream reads the DOM.
	//
	// Three things this costs, all handled below because the browser stops doing them for free:
	// a placeholder, a maxlength, and paste arriving as plain text.

	// The heading is a BADGE, not bold text (owner call 2026-08-17) — a tinted pill wearing the same
	// face as its lit button in the row above, so the pill in the message and the button that wrote
	// it are visibly one thing. Bold muted text read as the user's own emphasis instead.
	//
	// ⚠️ The tag's textContent must stay EXACTLY rubric(type): bodyText(), sectionBody() and
	// textBeforeCaret() all subtract the heading by matching that string. So the colon is still
	// here as a real element — CSS only HIDES it. A pill that ends in a colon reads as a label
	// waiting for its value, while the stored message still needs "💡 Suggestion: a dark mode" to
	// read as a sentence. Hidden, never dropped.
	function makeTag(type) {
		var tag = el("span", "fbTagClass");
		tag.setAttribute("contenteditable", "false");
		tag.setAttribute("data-type", type);
		tag.appendChild(document.createTextNode(
			GLYPH[type] + " " + getRes("feedback_type_" + type.toLowerCase())));
		tag.appendChild(el("span", "fbTagColonClass", ":"));
		return tag;
	}

	// One node's plain text, with <br> counted as the line break it draws.
	function nodeText(node) {
		if (node.nodeType === 3) { return node.nodeValue; }
		if (node.nodeName === "BR") { return "\n"; }
		var out = "";
		Array.prototype.forEach.call(node.childNodes, function (kid) { out += nodeText(kid); });
		return out;
	}

	// The message exactly as it will be stored: one section per LINE, so what lands in the database
	// reads the way the box reads. This is the ONE place the DOM becomes a string.
	//
	// It was a blank line between sections until the owner saw a real message written in it
	// (2026-08-17): a heading already announces where a section starts, so the extra line only
	// pushed a three-topic message past the height of the box. A section's own Enter breaks are
	// single newlines too, and that is fine — the headings are what mark the boundaries.
	function readMessage() {
		var parts = [], loose = "";
		Array.prototype.forEach.call(textArea.childNodes, function (node) {
			if (node.nodeType === 1 && node.classList.contains("fbSecClass")) {
				if (loose.trim()) { parts.push(loose); }
				loose = "";
				parts.push(nodeText(node));
			} else {
				loose += nodeText(node);
			}
		});
		if (loose.trim()) { parts.push(loose); }
		return parts.join("\n");
	}

	// What is left once the headings are struck out — the actual message. Send stays out of reach
	// until this is non-blank, so a box holding nothing but badges cannot be sent: the headings are
	// the app's own words, and a message made only of them says nothing.
	function bodyText() {
		var text = readMessage();
		textArea.querySelectorAll(".fbTagClass").forEach(function (tag) {
			text = text.split(tag.textContent).join("");
		});
		return text;
	}

	// The type the server is told about = the heading that comes FIRST. A report that opens with a
	// problem and closes with a suggestion is a problem report; what they reached for first is what
	// it is about. Every heading is in the message text either way, so nothing the writer said is
	// lost to this choice.
	function leadType() {
		var first = textArea.querySelector(".fbTagClass");
		return first ? first.getAttribute("data-type") : null;
	}

	// One section's words — everything in the block except its heading.
	function sectionBody(sec) {
		var tag = sec.querySelector(".fbTagClass");
		return tag ? nodeText(sec).replace(tag.textContent, "") : nodeText(sec);
	}

	// Lit badges and the Send button both read straight off the field — no stored selection to fall
	// out of step with it, and deleting a heading un-lights its badge for free.
	//
	// Lit is a CLASS, not aria-checked as it was while this was a radio group. These are buttons
	// that write a heading, not controls holding a state: pressing a lit one adds a SECOND section
	// rather than clearing the first, and neither aria-checked nor aria-pressed may promise a
	// toggle that would have to delete somebody's typed text to keep its word.
	function refresh() {
		var live = {};
		textArea.querySelectorAll(".fbTagClass").forEach(function (tag) {
			live[tag.getAttribute("data-type")] = true;
		});
		typeRow.querySelectorAll(".fbTypeBtnClass").forEach(function (btn) {
			btn.classList.toggle("fbTypeBtnOnClass", !!live[btn.getAttribute("data-type")]);
		});
		// The placeholder a textarea drew for itself. Driven by the MESSAGE, not by :empty — the
		// browser leaves a stray <br> behind when the last character goes, and an empty-looking box
		// with no invitation in it is exactly what this line exists to prevent.
		textArea.classList.toggle("fbTextEmptyClass", !readMessage().trim());
		sendBtn.disabled = sending || !leadType() || !bodyText().trim();
	}

	// The caret goes at the end of the LAST SECTION, not at the end of the field. Collapsing to the
	// end of the field itself leaves it parked on the container, one level above every section —
	// and `guardDelete` walks UP from where the caret is, so from there it finds no section and a
	// Backspace against a fresh heading was an ordinary character delete that ate the heading's
	// trailing space instead of dropping the mis-pressed badge. Caught by the smoke once undoing a
	// press became a thing a user does.
	function caretToEnd() {
		textArea.focus();
		var secs = textArea.querySelectorAll(".fbSecClass");
		var range = document.createRange();
		range.selectNodeContents(secs.length ? secs[secs.length - 1] : textArea);
		range.collapse(false);
		var sel = window.getSelection();
		sel.removeAllRanges();
		sel.addRange(range);
		textArea.scrollTop = textArea.scrollHeight;
	}

	// A badge is an object, not a place the caret may stand. contenteditable="false" already stops
	// it being typed INTO, but the browser will still park the caret at the very start of the block
	// — in front of the heading, where the next keystroke pushes text ahead of it and Home and
	// ArrowLeft land by design. So any collapsed caret at or before a heading is pushed to just
	// after it (owner call 2026-08-17: no cursor navigation into the badge).
	//
	// A selection the user DREW is left alone: dragging across a heading is how you delete it with
	// its words, which guardDelete deliberately allows.
	function normalizeCaret() {
		if (!layer || !textArea) { return; }
		var sel = window.getSelection();
		if (!sel.rangeCount || !sel.isCollapsed) { return; }
		var range = sel.getRangeAt(0);
		if (!textArea.contains(range.startContainer)) { return; }
		var sec = sectionAt(range.startContainer);
		var tag = sec && sec.querySelector(".fbTagClass");
		if (!tag) { return; }
		var after = document.createRange();
		after.setStartAfter(tag);
		after.collapse(true);
		if (range.compareBoundaryPoints(Range.START_TO_START, after) >= 0) { return; }
		sel.removeAllRanges();
		sel.addRange(after);
	}

	// Pressing a badge starts a new section at the end and parks the caret in it.
	//
	// EVERY press adds one (owner call 2026-08-17). It used to overwrite a heading that had no words
	// under it yet, on the theory that a bare heading was a mis-click — but that made the badges
	// behave like a picker again, where pressing Question and then Like left only Like, and the
	// whole point of writing into the message is that headings ACCUMULATE. A heading nobody wants
	// is one Backspace away; a heading silently eaten by the next press is not recoverable.
	function insertRubric(type) {
		// A box that reads as empty IS empty: whatever <br> the browser left behind is not content,
		// and appending a section after it would open the message with a blank line.
		if (!readMessage().trim()) { textArea.innerHTML = ""; }
		var sec = el("div", "fbSecClass");
		sec.appendChild(makeTag(type));
		// A real text node after the heading, because a caret needs somewhere to be: the block ends
		// in an uneditable element otherwise and there is no position after it to click into.
		sec.appendChild(document.createTextNode(" "));
		textArea.appendChild(sec);
		caretToEnd();
		refresh();
	}

	// ── Keeping the headings whole ───────────────────────────────────────────────────────────
	// A heading is not editable and cannot be half-eaten, and it only goes once its own words are
	// gone (owner call 2026-08-17). So a Backspace that would reach a heading is answered here:
	// if the section is empty the whole section goes at once, and if it still holds words the key
	// does nothing — the text has to be cleared first, which is what "as a whole" means.
	function sectionAt(node) {
		while (node && node !== textArea) {
			if (node.nodeType === 1 && node.classList.contains("fbSecClass")) { return node; }
			node = node.parentNode;
		}
		return null;
	}

	// Everything between the section's heading and the caret, as text. Blank means the caret is
	// sitting against the heading with nothing of the user's in between.
	function textBeforeCaret(sec, range) {
		var probe = range.cloneRange();
		probe.selectNodeContents(sec);
		probe.setEnd(range.startContainer, range.startOffset);
		var text = probe.toString();
		var tag = sec.querySelector(".fbTagClass");
		return tag ? text.replace(tag.textContent, "") : text;
	}

	function dropSection(sec) {
		var prev = sec.previousElementSibling;
		sec.remove();
		if (prev) {
			var range = document.createRange();
			range.selectNodeContents(prev);
			range.collapse(false);
			var sel = window.getSelection();
			sel.removeAllRanges();
			sel.addRange(range);
		} else if (!textArea.querySelector(".fbSecClass")) {
			textArea.innerHTML = "";
		}
		refresh();
	}

	function guardDelete(e) {
		if (e.key !== "Backspace" && e.key !== "Delete") { return; }
		var sel = window.getSelection();
		// A selection the user drew themselves takes the heading with its words, which IS deleting
		// it as a whole. Only the one-key case needs guarding.
		if (!sel.rangeCount || !sel.isCollapsed) { return; }
		var range = sel.getRangeAt(0);
		var sec = sectionAt(range.startContainer);
		if (e.key === "Backspace") {
			if (!sec || textBeforeCaret(sec, range).trim()) { return; }   // ordinary character delete
			e.preventDefault();
			if (!sectionBody(sec).trim()) { dropSection(sec); }
			return;
		}
		// Forward delete only ever threatens the NEXT section's heading, and only from the tail of
		// this one. Anywhere else it is an ordinary character delete.
		if (!sec) { return; }
		var after = range.cloneRange();
		after.selectNodeContents(sec);
		after.setStart(range.startContainer, range.startOffset);
		if (after.toString().trim()) { return; }
		var next = sec.nextElementSibling;
		if (!next || !next.classList.contains("fbSecClass")) { return; }
		e.preventDefault();
		if (!sectionBody(next).trim()) { next.remove(); refresh(); }
	}

	function build() {
		overlay = el("div", "msgOverlayClass");
		box = el("div", "msgBoxClass fbCardClass");
		box.setAttribute("role", "dialog");
		box.setAttribute("aria-modal", "true");
		overlay.appendChild(box);

		box.appendChild(el("div", "msgBoxTitleClass", getRes("feedback_title")));

		// Five badges, no default. Each writes its heading into the message, so a message can carry
		// more than one — which is why this is no longer the radio group it started as. A pre-lit
		// badge would quietly turn every unread choice into that answer, and now would put words in
		// the box nobody typed.
		typeRow = el("div", "fbTypeRowClass");
		typeRow.id = "feedbackTypeRowId";
		typeRow.setAttribute("role", "group");
		TYPES.forEach(function (type) {
			var btn = el("button", "fbTypeBtnClass");
			btn.type = "button";
			btn.setAttribute("data-type", type);
			btn.appendChild(el("span", "fbTypeIcoClass", GLYPH[type]));
			btn.appendChild(el("span", null, getRes("feedback_type_" + type.toLowerCase())));
			// The question each badge asks. It was the textarea's placeholder until the badges
			// started filling that box themselves — a placeholder under text is a placeholder
			// nobody reads — so it moved to where it can still be found.
			$(btn).assignTooltip("feedback_ph_" + type.toLowerCase());
			btn.addEventListener("click", function () { insertRubric(type); });
			typeRow.appendChild(btn);
		});
		box.appendChild(typeRow);

		textArea = el("div", "fbTextClass");
		textArea.id = "feedbackTextId";
		textArea.setAttribute("contenteditable", "true");
		textArea.setAttribute("role", "textbox");
		textArea.setAttribute("aria-multiline", "true");
		textArea.setAttribute("aria-label", getRes("feedback_title"));
		// The dialog must not open WORDLESS — an empty box, five unlit badges and a greyed-out Send,
		// with the one sentence explaining the order ("pick a type, then write") sitting where only a
		// server reject the disabled button makes unreachable could show it. The instruction has to
		// be where the reader is. It is the only placeholder now: the five per-type questions moved
		// to the badges' tooltips, because from the first badge press this box is never empty and a
		// placeholder under text is a placeholder nobody reads.
		// Drawn by CSS from this attribute, because an editable div has no placeholder of its own.
		textArea.setAttribute("data-ph", getRes("feedback_ph_start"));
		textArea.addEventListener("input", refresh);
		textArea.addEventListener("keydown", guardDelete);
		// On the document, because selectionchange fires nowhere else — and cheap enough to leave
		// armed: normalizeCaret returns on the first line whenever the dialog is closed.
		document.addEventListener("selectionchange", normalizeCaret);
		// Enter stays INSIDE the section it was pressed in. Left to itself the browser splits the
		// block, and a plain line break would silently become a new section — spaced like one,
		// serialized like one, and headed by nothing.
		textArea.addEventListener("keydown", function (e) {
			if (e.key !== "Enter" || e.shiftKey) { return; }
			e.preventDefault();
			document.execCommand("insertLineBreak");
		});
		// The maxlength a textarea enforced for free. The server truncates at 2000 too, but finding
		// out there is what a lost tail feels like.
		textArea.addEventListener("beforeinput", function (e) {
			var adds = e.inputType && e.inputType.indexOf("insert") === 0;
			if (adds && readMessage().length >= 2000) { e.preventDefault(); }
		});
		// Paste arrives as plain text or it arrives as somebody else's markup — headings included,
		// which would put a heading in the message that no badge ever wrote.
		textArea.addEventListener("paste", function (e) {
			e.preventDefault();
			var text = (e.clipboardData || window.clipboardData).getData("text") || "";
			document.execCommand("insertText", false, text.slice(0, 2000 - readMessage().length));
		});
		box.appendChild(textArea);

		// Asked ONLY when the account has none of its own: a temp user's typed address is the only
		// way an answer could ever reach them, and a registered user retyping what we already have
		// would be a question with no purpose. Never prefilled.
		if (!window.user_has_email) {
			var emailWrap = el("label", "fbEmailWrapClass");
			emailWrap.appendChild(el("span", "fbEmailLabelClass", getRes("feedback_email_label")));
			emailField = document.createElement("input");
			emailField.id = "feedbackEmailId";
			emailField.className = "fbEmailClass";
			emailField.type = "email";
			emailField.maxLength = 254;
			emailWrap.appendChild(emailField);
			box.appendChild(emailWrap);
		}

		var foot = el("div", "msgBoxFootClass fbFootClass");
		var cancel = el("button", "pl-btn-quiet fbCancelBtnClass", getRes("feedback_cancel"));
		cancel.type = "button";
		cancel.addEventListener("click", function () { close("cancel"); });
		sendBtn = el("button", "pl-btn-pill fbSendBtnClass", getRes("feedback_send"));
		sendBtn.id = "feedbackSendBtnId";
		sendBtn.type = "button";
		sendBtn.addEventListener("click", send);
		foot.appendChild(cancel);
		foot.appendChild(sendBtn);
		box.appendChild(foot);

		// Tab cycles inside the card: the page behind the backdrop stays visible but unreachable.
		// Wider than onboarding's button-only trap — this card holds a writing surface and an input
		// too. [contenteditable] is in the list because the message field is a div now: it is
		// tabbable, but it answers to none of the other four selectors.
		box.addEventListener("keydown", function (e) {
			if (e.key !== "Tab") { return; }
			var items = box.querySelectorAll("button, a[href], textarea, input, [contenteditable], [tabindex]");
			if (!items.length) { e.preventDefault(); return; }
			var first = items[0], last = items[items.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault(); last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault(); first.focus();
			}
		});
	}

	function open(source) {
		if (layer) { return; }
		if (!overlay) { build(); }
		// Every open starts unpicked (owner call 2026-08-17). Headings with nothing written under
		// them are the residue of a press somebody thought better of, not a draft, so they go —
		// which is what makes "select, close, reopen ⇒ nothing selected" literally true now that
		// the selection lives in the text. A message with actual words in it still survives an
		// accidental Esc, exactly as before; that is the whole reason this DOM is kept.
		if (!bodyText().trim()) { textArea.innerHTML = ""; }
		context = collectContext(source);
		var focusToken = PL.focus.remember();
		document.body.appendChild(overlay);
		layer = PL.layers.push({
			el: box,
			onClose: function () {
				layer = null;
				overlay.remove();     // detached, not destroyed — the typed text survives
				if (focusToken) { focusToken.restore({ ifWithin: box }); }
			}
		});
		refresh();
		// A surviving draft is resumed where it was left off; an empty box hands focus to the
		// badges, which are the step before writing.
		if (readMessage().trim()) { caretToEnd(); } else { typeRow.firstChild.focus(); }
	}

	function close(reason) {
		if (layer) { layer.close(reason || "api"); }
	}

	function send() {
		var type = leadType();
		if (sending || !type) { return; }
		sending = true;
		refresh();
		$.ajax({
			url: "/api/feedback",
			type: "POST",
			contentType: "application/json",
			// Named, not guessed: an empty 200 body still arrives as application/json, and jQuery's
			// "intelligent guess" would run the JSON converter over "" and route a SUCCESSFUL send
			// into the error handler. The endpoint answers {"ok":true} for the same reason.
			dataType: "json",
			data: JSON.stringify({
				// The lead rubric names the row; the message carries every rubric verbatim, so a
				// mixed report reads as what it is on the admin page whatever this single column says.
				type: type,
				message: readMessage(),
				contactEmail: emailField ? emailField.value : null,
				context: context
			}),
			success: function () {
				sending = false;
				close("sent");
				// Cleared only now: up to this point the text is the user's, not ours. Emptying the
				// box un-lights every badge on its own — that is the point of deriving them from it.
				textArea.innerHTML = "";
				if (emailField) { emailField.value = ""; }
				refresh();
				showToast(getRes("feedback_thanks"));
			},
			error: function (jqXHR) {
				// The dialog STAYS OPEN with the text intact — a rejected send must never be a
				// lost message. The server has already resolved its key to the request locale.
				sending = false;
				refresh();
				var msg = "";
				try { msg = JSON.parse(jqXHR.responseText || "{}").error || ""; } catch (e) { msg = ""; }
				showMessage(msg || getRes("error"));
			}
		});
	}

	// ── The door ─────────────────────────────────────────────────────────────────────────────
	// ONE door since 2026-08-31 (owner call): the "Send feedback" panel in the right pane, under
	// My Progress. It is server-rendered in main.html and wired below.
	//
	// There were THREE round 💬 pills before that — the tabs row, every Learn screen, the Practice
	// finish card — built by a `cornerButton(source, extraClass)` factory this file exported. The
	// two built screens needed their own copies because the tabs row HIDES for the whole of a drill,
	// which is exactly the problem the right pane does not have: no view switch touches it, so one
	// door there covers every screen the three used to cover between them. The factory, the
	// .fbCornerClass marker and every `source` but this one went with them.
	// History: docs/project/user-feedback-feature.md.

	// readMessage is out here for the smoke harness: the field is a div now, so there is no .value
	// for a test to read, and asserting against innerHTML would test the browser's editing quirks
	// rather than what the user wrote.
	window.FEEDBACK = { open: open, close: close,
		readMessage: function () { return textArea ? readMessage() : ""; } };

	$(document).ready(function () {
		// The right pane's panel. Its label is painted here rather than from a message bundle: the
		// key it needs, feedback_menu, is the one the tooltip has always used, so the button and its
		// tooltip cannot drift apart into two different names for the same act.
		var panel = $("#feedbackPanelBtnId");
		var label = getRes("feedback_menu");
		if (label) { $("#feedbackPanelLblId").text(label); }
		panel.attr("aria-label", label);
		panel.click(function (e) {
			e.stopPropagation();
			open("pane");
		});
	});

})();
